import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuthentication from "../auth/authHelper";
import Loader from "react-loader-spinner";
import {
  MdOutlineEmail,
  MdLockOutline,
  MdVisibility,
  MdVisibilityOff,
  MdCheck,
  MdBolt,
  MdOutlineShield,
  MdWaterDrop,
  MdOutlineInfo,
} from "react-icons/md";
import { GoArrowRight } from "react-icons/go";
import { WR } from "../components/ui/tokens";

const Login = () => {
  const { t } = useTranslation();
  const { userLogin } = useAuthentication();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // UI-only state (no auth behaviour change): password reveal, remember toggle,
  // and which field is focused (for the focus glow).
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [focused, setFocused] = useState(null);
  const [forgotMsg, setForgotMsg] = useState(false);
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth < 880 : false
  );

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 880);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setIsLoading(true);
    const { success, message } = await userLogin(email, password);
    setIsLoading(false);
    if (success) {
      navigate("/start");
    } else {
      setLoginError(message);
    }
  };

  const fieldWrap = (name) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    height: 54,
    padding: "0 14px",
    borderRadius: 14,
    background: "#fff",
    border: `1.5px solid ${focused === name ? WR.color.brand : WR.color.border}`,
    boxShadow:
      focused === name
        ? "0 0 0 4px rgba(61,69,134,0.15), 0 10px 26px -12px rgba(61,69,134,0.45)"
        : "none",
    transition: "border-color .15s ease, box-shadow .15s ease",
  });
  const inputStyle = {
    flex: 1,
    minWidth: 0,
    border: 0,
    outline: "none",
    background: "transparent",
    fontSize: 15,
    fontWeight: 600,
    color: WR.color.encre,
    fontFamily: WR.font.sans,
  };
  const fieldLabel = {
    display: "block",
    fontSize: 13,
    fontWeight: 700,
    color: WR.color.inkMuted,
    margin: "0 0 7px",
  };

  return (
    <div style={{ display: "flex", minHeight: "100dvh", fontFamily: WR.font.sans, background: WR.color.bg }}>
      {/* LEFT — indigo hero (hidden on narrow screens) */}
      {!isNarrow && (
        <div
          style={{
            flex: "1 1 0",
            position: "relative",
            overflow: "hidden",
            background: "linear-gradient(160deg, #3D4586 0%, #2F366B 100%)",
            color: "#fff",
            padding: "48px clamp(40px,4vw,64px)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          {/* decorative shapes */}
          <div aria-hidden="true" style={{ position: "absolute", top: -90, right: -70, width: 360, height: 360, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
          <div aria-hidden="true" style={{ position: "absolute", bottom: -110, left: -90, width: 320, height: 320, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
          <div aria-hidden="true" style={{ position: "absolute", top: "42%", right: "16%", width: 96, height: 96, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.18)" }} />

          {/* logo */}
          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(255,255,255,0.14)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MdWaterDrop size={22} />
            </span>
            <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: 3 }}>WOSH</span>
          </div>

          {/* headline */}
          <div style={{ position: "relative", maxWidth: 440 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "6px 13px", borderRadius: 999, background: "rgba(255,255,255,0.12)", fontSize: 11, fontWeight: 800, letterSpacing: 1.6, textTransform: "uppercase" }}>
              <span style={{ width: 7, height: 7, borderRadius: 999, background: WR.color.mintDeep }} />
              Point of sale
            </span>
            <h1 style={{ fontSize: "clamp(38px,4vw,54px)", fontWeight: 800, letterSpacing: -1, margin: "20px 0 14px" }}>
              {t("LOGIN_HERO_TITLE")}
            </h1>
            <p style={{ fontSize: 16, lineHeight: 1.6, color: "rgba(255,255,255,0.78)", margin: 0 }}>
              {t("LOGIN_HERO_SUBTITLE")}
            </p>
          </div>

          {/* footer chips */}
          <div style={{ position: "relative", display: "flex", flexWrap: "wrap", gap: 24, fontSize: 13, color: "rgba(255,255,255,0.85)" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
              <MdBolt size={16} color={WR.color.mintDeep} /> {t("LOGIN_FEAT_1")}
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
              <MdOutlineShield size={16} color={WR.color.mintDeep} /> {t("LOGIN_FEAT_2")}
            </span>
          </div>
        </div>
      )}

      {/* RIGHT — form */}
      <div style={{ flex: "1 1 0", display: "flex", alignItems: "center", justifyContent: "center", padding: "32px clamp(20px,5vw,64px)" }}>
        <div style={{ width: "100%", maxWidth: 400 }}>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.6, textTransform: "uppercase", color: WR.color.mintDeep, marginBottom: 8 }}>
            {t("LOGIN_EYEBROW")}
          </div>
          <h2 style={{ fontSize: "clamp(28px,3.4vw,34px)", fontWeight: 800, letterSpacing: -0.6, color: WR.color.encre, margin: "0 0 6px" }}>
            {t("LOGIN_TITLE")}
          </h2>
          <p style={{ fontSize: 15, color: WR.color.inkMuted, margin: "0 0 26px" }}>
            {t("LOGIN_SUBTITLE")}
          </p>

          {loginError && (
            <div
              role="alert"
              style={{ padding: "12px 16px", borderRadius: 12, background: "#FDECEC", border: "1px solid #F3C9CF", color: WR.color.danger, fontSize: 14, fontWeight: 600, marginBottom: 18 }}
            >
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="email" style={fieldLabel}>{t("EMAIL")}</label>
              <div style={fieldWrap("email")}>
                <MdOutlineEmail size={19} color={WR.color.inkSoft} style={{ flexShrink: 0 }} />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocused("email")}
                  onBlur={() => setFocused(null)}
                  placeholder="hello@wosh.be"
                  autoComplete="username"
                  required
                  style={inputStyle}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" style={fieldLabel}>{t("PASSWORD")}</label>
              <div style={fieldWrap("password")}>
                <MdLockOutline size={19} color={WR.color.inkSoft} style={{ flexShrink: 0 }} />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocused("password")}
                  onBlur={() => setFocused(null)}
                  autoComplete="current-password"
                  required
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  style={{ border: 0, background: "none", padding: 0, cursor: "pointer", color: WR.color.inkSoft, display: "inline-flex", flexShrink: 0 }}
                >
                  {showPassword ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
                </button>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "16px 0 22px", gap: 12, flexWrap: "wrap" }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 9, cursor: "pointer", fontSize: 14, fontWeight: 600, color: WR.color.encre }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{ position: "absolute", opacity: 0, width: 1, height: 1 }}
                />
                <span
                  aria-hidden="true"
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 6,
                    flexShrink: 0,
                    background: rememberMe ? WR.color.brand : "#fff",
                    border: `1.5px solid ${rememberMe ? WR.color.brand : WR.color.border}`,
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {rememberMe && <MdCheck size={14} />}
                </span>
                {t("REMEMBER_ME")}
              </label>
              <button
                type="button"
                onClick={() => setForgotMsg(true)}
                style={{ fontSize: 14, fontWeight: 700, color: WR.color.brand, background: "none", border: 0, padding: 0, cursor: "pointer", fontFamily: WR.font.sans }}
              >
                {t("FORGOT_PASSWORD")}
              </button>
            </div>

            {forgotMsg && (
              <div
                role="status"
                style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "11px 14px", borderRadius: 12, background: "#FFF8E1", border: "1px solid #F6E0A8", color: "#8a6d2f", fontSize: 13, fontWeight: 600, marginBottom: 18 }}
              >
                <MdOutlineInfo size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                {t("FORGOT_PASSWORD_HELP")}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: "100%",
                height: 56,
                borderRadius: 999,
                background: WR.color.brand,
                color: "#fff",
                border: 0,
                fontSize: 16,
                fontWeight: 700,
                boxShadow: isLoading ? "none" : WR.shadow.cta,
                opacity: isLoading ? 0.7 : 1,
                cursor: isLoading ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 9,
                fontFamily: WR.font.sans,
              }}
            >
              {t("SIGN_IN")}
              {isLoading ? (
                <Loader type="Oval" color="#ffffff" height={20} width={20} />
              ) : (
                <GoArrowRight size={18} />
              )}
            </button>
          </form>

          <div style={{ textAlign: "center", fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: WR.color.inkSoft, marginTop: 26 }}>
            WOSH POS v2
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
